// 👁️ 알림톡 미리보기 다이얼로그 컴포넌트
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Avatar,
  Box,
  Stack,
  Paper,
  Alert,
  Chip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import type { Application } from '../../../types/application';

interface AlertPreviewDialogProps {
  open: boolean;
  application: Application;
  alertPreviewMessage: string;
  perDevicePreviews?: Array<{
    deviceId: string;
    deviceModel: string;
    grade?: string | null;
    priceStr: string;
    event: string;
    message: string;
    errors: string[];
  }>;
  isStatusSaving: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  // 🆕 정보입력요청 전용 옵션
  isInfoRequest?: boolean;
}

/**
 * 알림톡 미리보기 다이얼로그 컴포넌트
 * - 발송 대상 정보 표시
 * - 메시지 미리보기
 * - 발송 확인 및 취소 버튼
 */
export const AlertPreviewDialog: React.FC<AlertPreviewDialogProps> = ({
  open,
  application,
  alertPreviewMessage,
  perDevicePreviews,
  isStatusSaving,
  onClose,
  onConfirm,
  isInfoRequest = false,
}) => {
  // 🆕 정보입력요청인 경우 색상/텍스트 변경
  const themeColor = isInfoRequest ? '#7c3aed' : '#1976d2';
  const bgLight = isInfoRequest ? 'rgba(124, 58, 237, 0.05)' : 'primary.50';
  const bgLighter = isInfoRequest ? 'rgba(124, 58, 237, 0.03)' : '#f0f7ff';
  const textColor = isInfoRequest ? '#5b21b6' : '#1565c0';
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          color: themeColor,
          fontWeight: 700,
          fontSize: '1.25rem',
          pb: 1,
        }}
      >
        <Avatar
          sx={{
            bgcolor: themeColor,
            color: 'white',
            width: 48,
            height: 48,
          }}
        >
          <VisibilityIcon />
        </Avatar>
        {isInfoRequest ? '정보입력 요청 알림톡' : '알림톡 메시지 미리보기'}
        {isInfoRequest && (
          <Chip
            label="거래처 전용"
            size="small"
            sx={{
              bgcolor: themeColor,
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.7rem',
            }}
          />
        )}
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              p: 2,
              bgcolor: bgLight,
              borderRadius: 2,
              border: `1px solid ${isInfoRequest ? 'rgba(124, 58, 237, 0.2)' : '#bbdefb'}`,
            }}
          >
            <Stack spacing={1}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: themeColor }}>
                📱 발송 대상
              </Typography>
              <Typography variant="body2">
                <strong>{application.user_name}</strong> ({application.phone_number})
              </Typography>
              {/* 🆕 정보입력요청인 경우 기기 정보도 표시 */}
              {isInfoRequest && application.devices && application.devices.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  기기: {application.devices.map(d => d.deviceModel).join(', ')}
                </Typography>
              )}
            </Stack>
          </Box>

          <Box
            sx={{
              p: 2,
              bgcolor: bgLighter,
              borderRadius: 2,
              border: `1px solid ${isInfoRequest ? 'rgba(124, 58, 237, 0.15)' : '#bbdefb'}`,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} sx={{ color: themeColor, mb: 1 }}>
              💬 메시지 내용
            </Typography>
            {perDevicePreviews && perDevicePreviews.length > 0 ? (
              <Box sx={{ display: 'grid', gap: 1.5 }}>
                {perDevicePreviews.map((p) => (
                  <Paper key={p.deviceId} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e1e8ed', bgcolor: 'rgba(250,252,255,0.9)', boxShadow: '0 4px 18px rgba(0,0,0,0.06)' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight={800} sx={{ letterSpacing: '-0.2px' }}>
                        {p.deviceModel} · {p.priceStr}원
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip label={p.grade ? `${p.grade}등급` : '등급미정'} size="small" sx={{ height: 20, bgcolor: p.grade === 'A' ? '#10b981' : p.grade === 'B' ? '#3b82f6' : p.grade === 'C' ? '#f59e0b' : p.grade === 'D' ? '#ef4444' : '#90a4ae', color: '#fff' }} />
                        <Chip label={p.event} size="small" sx={{ bgcolor: '#1976d2', color: '#fff', height: 20 }} />
                      </Box>
                    </Box>
                    {p.errors.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {p.errors.map((e, idx) => (
                          <Chip key={idx} label={e} color="error" size="small" sx={{ mr: 0.5 }} />
                        ))}
                      </Box>
                    )}
                    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f9fbff', border: '1px solid #e1e8ed', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#1565c0', lineHeight: 1.6 }}>
                        {p.message}
                      </Typography>
                    </Paper>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  whiteSpace: 'pre-line',
                  borderRadius: 2,
                  bgcolor: 'white',
                  border: '1px solid #e1e8ed',
                }}
              >
                <Typography variant="body2" sx={{ color: textColor, lineHeight: 1.6 }}>
                  {alertPreviewMessage}
                </Typography>
              </Paper>
            )}
            
            {/* 🆕 정보입력요청인 경우 버튼 미리보기 */}
            {isInfoRequest && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  📎 알림톡 하단 버튼:
                </Typography>
                <Chip
                  label="정보 입력하기"
                  sx={{
                    bgcolor: '#f5c107',
                    color: '#000',
                    fontWeight: 600,
                  }}
                />
              </Box>
            )}
          </Box>

          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">
              {isInfoRequest ? (
                <>
                  <strong>⚠️ 주의사항:</strong> "알림톡 발송" 버튼을 클릭하면 위 메시지가 고객에게 즉시 발송됩니다.
                  <br />
                  고객은 알림톡 내 버튼을 통해 주소 및 계좌 정보를 입력할 수 있습니다.
                </>
              ) : (
                <>
                  <strong>⚠️ 주의사항:</strong> 아래 "저장 & 알림톡 전송" 버튼을 클릭하면 상태 변경과 함께 위 메시지가 고객에게 즉시 발송됩니다.
                </>
              )}
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isStatusSaving}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
          }}
        >
          취소
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          startIcon={<SendIcon />}
          disabled={isStatusSaving}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            bgcolor: themeColor,
            boxShadow: `0 4px 12px ${isInfoRequest ? 'rgba(124, 58, 237, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
            '&:hover': {
              bgcolor: isInfoRequest ? '#6d28d9' : '#1565c0',
              boxShadow: `0 6px 20px ${isInfoRequest ? 'rgba(124, 58, 237, 0.4)' : 'rgba(25, 118, 210, 0.4)'}`,
            },
          }}
        >
          {isStatusSaving ? '전송 중...' : (isInfoRequest ? '알림톡 발송' : '저장 & 알림톡 전송')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};